import { useState } from 'react';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { formatDate, today } from '../../utils/formatDate';
import { formatMoney } from '../../utils/formatMoney';
import LoadingSpinner from '../../components/LoadingSpinner';
import { aiAuthorityApi, closeReadinessApi, type AuthorityConfigDto, type CloseReadinessDto } from '../../api/aiAuthority';
import { getWorkspaceId } from '../../api/workspaces';

// --- Types ---

interface DecisionLogEntry {
  id: string;
  timestamp: string;
  agentName: string;
  authorityLevel: 'observe' | 'suggest' | 'act-with-review' | 'act-autonomous';
  action: string;
  confidence: number;
  inputs: string;
  policyVersion: string;
  outcome: string;
  reversible: boolean;
  reviewerId: string | null;
  reviewedAt: string | null;
  accepted: boolean | null;
}

interface DecisionLogResponse {
  entries: DecisionLogEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
}

interface ScannerResult {
  type: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  affectedRecords?: string[];
  suggestedAction?: string;
}

interface ScannerSummary {
  totalIssues: number;
  criticalCount: number;
  warningCount: number;
  infoCount: number;
  results: ScannerResult[];
}

// --- Payment matching types ---

interface PaymentMatch {
  journalEntryId: number;
  documentNumber: string;
  date: string;
  amount: number;
  personId: number | null;
  personName: string | null;
  content: string | null;
  amountDifference: number;
}

// --- Tabs ---

type TabId = 'decision-log' | 'exceptions' | 'agent-config' | 'policy-versions' | 'commander';

const TABS: { id: TabId; label: string }[] = [
  { id: 'commander', label: 'Commander' },
  { id: 'decision-log', label: 'Decision Log' },
  { id: 'exceptions', label: 'Exceptions' },
  { id: 'agent-config', label: 'Agent Config' },
  { id: 'policy-versions', label: 'Policy Versions' },
];

// --- Authority level badge ---

function AuthorityBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    observe: 'bg-gray-100 text-gray-700',
    suggest: 'bg-primary-100 text-primary-700',
    'act-with-review': 'bg-amber-100 text-amber-700',
    'act-autonomous': 'bg-green-100 text-green-700',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[level] ?? 'bg-gray-100 text-gray-700'}`}>
      {level}
    </span>
  );
}

// --- Severity badge ---

function SeverityBadge({ severity }: { severity: string }) {
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-700',
    warning: 'bg-amber-100 text-amber-700',
    info: 'bg-primary-100 text-primary-700',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[severity] ?? 'bg-gray-100 text-gray-700'}`}>
      {severity}
    </span>
  );
}

// --- Confidence bar ---

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const color = pct >= 90 ? 'bg-green-500' : pct >= 70 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500">{pct}%</span>
    </div>
  );
}

// --- Decision Log Tab ---

function DecisionLogTab() {
  const [page, setPage] = useState(1);
  const [filterAgent, setFilterAgent] = useState('');
  const [filterReviewed, setFilterReviewed] = useState<'all' | 'reviewed' | 'unreviewed'>('all');
  const pageSize = 20;
  const hasWorkspace = !!getWorkspaceId();

  const { data, isLoading } = useQuery<DecisionLogResponse>({
    queryKey: ['decision-log', page, filterAgent, filterReviewed],
    queryFn: () => api.get('/decision-log', {
      page,
      pageSize,
      agentName: filterAgent || undefined,
      reviewed: filterReviewed === 'all' ? undefined : filterReviewed === 'reviewed',
    }),
    staleTime: 30_000,
    enabled: hasWorkspace,
  });

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="Filter by agent name..."
          value={filterAgent}
          onChange={(e) => { setFilterAgent(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-48"
        />
        <select
          value={filterReviewed}
          onChange={(e) => { setFilterReviewed(e.target.value as typeof filterReviewed); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">All</option>
          <option value="reviewed">Reviewed</option>
          <option value="unreviewed">Unreviewed</option>
        </select>
        {data && (
          <span className="text-xs text-gray-400">
            {data.totalCount} total entries
          </span>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <LoadingSpinner />
      ) : !data?.entries?.length ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          No decision log entries found.
          <br />
          <span className="text-xs">AI actions will appear here as they are performed.</span>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="dense w-full">
              <thead>
                <tr>
                  <th className="text-left">Timestamp</th>
                  <th className="text-left">Agent</th>
                  <th className="text-left">Authority</th>
                  <th className="text-left">Action</th>
                  <th className="text-left">Confidence</th>
                  <th className="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-primary-50 cursor-pointer">
                    <td className="whitespace-nowrap">{formatDate(entry.timestamp)}</td>
                    <td className="font-medium">{entry.agentName}</td>
                    <td><AuthorityBadge level={entry.authorityLevel} /></td>
                    <td className="max-w-48 truncate">{entry.action}</td>
                    <td><ConfidenceBar value={entry.confidence} /></td>
                    <td>
                      {entry.accepted === null && (
                        <span className="text-xs text-amber-600 font-medium">Pending review</span>
                      )}
                      {entry.accepted === true && (
                        <span className="text-xs text-green-600 font-medium">Accepted</span>
                      )}
                      {entry.accepted === false && (
                        <span className="text-xs text-red-600 font-medium">Rejected</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.totalCount > pageSize && (
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-gray-400">
                Page {page} of {Math.ceil(data.totalCount / pageSize)}
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  disabled={page * pageSize >= data.totalCount}
                  onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// --- Exceptions Tab ---

function ExceptionsTab() {
  const hasWorkspace = !!getWorkspaceId();

  const { data: scannerData, isLoading: scannerLoading } = useQuery<ScannerSummary>({
    queryKey: ['scanner-results'],
    queryFn: () => api.get('/scanners/summary'),
    staleTime: 10_000,
    enabled: hasWorkspace,
  });

  const { data: closeReadiness, isLoading: readinessLoading } = useQuery<CloseReadinessDto>({
    queryKey: ['close-readiness'],
    queryFn: closeReadinessApi.get,
    staleTime: 60_000,
    retry: false,
    enabled: hasWorkspace,
  });

  if (scannerLoading && readinessLoading) return <LoadingSpinner />;

  const results = scannerData?.results ?? [];
  const scannerCritical = scannerData?.criticalCount ?? closeReadiness?.criticalCount ?? 0;
  const scannerWarnings = scannerData?.warningCount ?? closeReadiness?.warningCount ?? 0;
  const scannerInfo = scannerData?.infoCount ?? 0;

  // Combine scanner findings and close readiness blockers
  const readinessScore = closeReadiness?.overallScore;
  const blockers = closeReadiness?.blockers ?? [];

  // Group findings by severity
  const critical = results.filter(r => r.severity === 'critical');
  const warnings = results.filter(r => r.severity === 'warning');
  const info = results.filter(r => r.severity === 'info');

  return (
    <div className="space-y-6">
      {/* Close Readiness Score — prominent display */}
      {readinessScore !== undefined && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Period Close Readiness</h3>
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${
              readinessScore >= 80 ? 'bg-green-100 text-green-700' :
              readinessScore >= 50 ? 'bg-amber-100 text-amber-700' :
              'bg-red-100 text-red-700'
            }`}>
              {readinessScore}%
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
            <div
              className={`h-full rounded-full transition-all ${
                readinessScore >= 80 ? 'bg-green-500' :
                readinessScore >= 50 ? 'bg-amber-500' :
                'bg-red-500'
              }`}
              style={{ width: `${readinessScore}%` }}
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center text-xs">
            <div>
              <div className="font-semibold text-gray-700">{closeReadiness?.bankMatchPercentage ?? 100}%</div>
              <div className="text-gray-400">Bank Match</div>
            </div>
            <div>
              <div className="font-semibold text-gray-700">{closeReadiness?.invoicePostPercentage ?? 100}%</div>
              <div className="text-gray-400">Invoice Post</div>
            </div>
            <div>
              <div className="font-semibold text-gray-700">{closeReadiness?.unresolvedAlertsCount ?? 0}</div>
              <div className="text-gray-400">Unresolved Alerts</div>
            </div>
            <div>
              <div className="font-semibold text-gray-700">{closeReadiness?.missingAccrualsEstimate ?? 0}</div>
              <div className="text-gray-400">Missing Accruals</div>
            </div>
          </div>
          {blockers.length > 0 && (
            <div className="mt-3 space-y-1">
              {blockers.map((b, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-red-600">
                  <span className="mt-0.5">⚠</span>
                  <span>{b}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-red-600">{scannerCritical}</div>
          <div className="text-xs text-red-500 font-medium">Critical</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-amber-600">{scannerWarnings}</div>
          <div className="text-xs text-amber-500 font-medium">Warnings</div>
        </div>
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-primary-600">{scannerInfo}</div>
          <div className="text-xs text-primary-500 font-medium">Info</div>
        </div>
      </div>

      {results.length === 0 && blockers.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          No scanner exceptions detected.
          <br />
          <span className="text-xs">Run scanners to check for issues.</span>
        </div>
      ) : (
        <div className="space-y-2">
          {critical.map((r, i) => (
            <ExceptionCard key={`critical-${i}`} result={r} />
          ))}
          {warnings.map((r, i) => (
            <ExceptionCard key={`warning-${i}`} result={r} />
          ))}
          {info.map((r, i) => (
            <ExceptionCard key={`info-${i}`} result={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function getEntityRoute(affectedRecord: string): string | null {
  const slashIndex = affectedRecord.indexOf('/');
  if (slashIndex < 0) return null;
  const type = affectedRecord.substring(0, slashIndex).toLowerCase();
  const id = affectedRecord.substring(slashIndex + 1);
  switch (type) {
    case 'invoicesmade':
    case 'invoicesreceived':
      return `/invoices/${id}`;
    case 'journalentries':
      return `/journal/${id}`;
    case 'bankoperations':
      return '/banking';
    case 'persons':
      return '/persons';
    case 'offsets':
      return '/offsets';
    default:
      return null;
  }
}

function formatEntityType(type: string): string {
  return type.replace(/([A-Z])/g, ' $1').trim();
}

function ExceptionCard({ result }: { result: ScannerResult }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Settlement state
  const [findingPayments, setFindingPayments] = useState(false);
  const [showMatches, setShowMatches] = useState(false);
  const [matches, setMatches] = useState<PaymentMatch[]>([]);
  const [matchesError, setMatchesError] = useState<string | null>(null);
  const [settling, setSettling] = useState<number | null>(null); // journalEntryId being settled
  const [settleResult, setSettleResult] = useState<{ success: boolean; message: string } | null>(null);

  // Detect if this is an overdue invoice finding
  const isOverdueInvoice = React.useMemo(() => {
    const hasInvoiceRecord = result.affectedRecords?.some(r => r.toLowerCase().startsWith('invoicesmade/'));
    const titleLower = (result.title ?? '').toLowerCase();
    const descLower = (result.description ?? '').toLowerCase();
    const mentionsOverdue = titleLower.includes('overdue') || titleLower.includes('veluojantis') ||
      descLower.includes('overdue') || descLower.includes('veluojantis') ||
      descLower.includes('neapmoket');
    return !!(hasInvoiceRecord && mentionsOverdue);
  }, [result]);

  // Extract invoice journal entry ID from affectedRecords
  const invoiceJournalEntryId = React.useMemo(() => {
    if (!isOverdueInvoice || !result.affectedRecords) return null;
    const invoiceRecord = result.affectedRecords.find(r => r.toLowerCase().startsWith('invoicesmade/'));
    if (!invoiceRecord) return null;
    const slashIndex = invoiceRecord.indexOf('/');
    const id = invoiceRecord.substring(slashIndex + 1);
    const parsed = parseInt(id, 10);
    return isNaN(parsed) ? null : parsed;
  }, [isOverdueInvoice, result.affectedRecords]);

  // Parse evidence from result for receivables account and person info
  // TODO: used in future settlement flow
  // TODO: used in future settlement flow
  // TODO: used in future settlement flow — suppress unused warning
  const _evidence = React.useMemo(() => {
    const defaults = {
      personId: null as number | null,
      receivablesAccount: null as number | null,
      invoiceDocNumber: null as string | null,
      unpaidAmount: null as number | null,
    };
    try {
      // Extract unpaid amount from description pattern: "neapmoketa likutis: X EUR"
      const descMatch = result.description?.match(/neapmoketa likutis:\s*([\d.]+)\s*EUR/i);
      const unpaidFromDesc = descMatch ? parseFloat(descMatch[1] ?? '0') : null;

      // Extract invoice doc number from description: "Pardavimo saskaita SF001 ..."
      const docMatch = result.description?.match(/Pardavimo saskaita\s+(\S+)/i) ??
                       result.description?.match(/saskaita\s+(\S+)/i);
      const docNumber = docMatch ? docMatch[1] : null;

      // Extract personId from affectedRecords format: "persons/ID"
      let personIdFromRecord: number | null = null;
      if (result.affectedRecords) {
        const personRecord = result.affectedRecords.find(r => r.toLowerCase().startsWith('persons/'));
        if (personRecord) {
          const slashIndex = personRecord.indexOf('/');
          const id = parseInt(personRecord.substring(slashIndex + 1), 10);
          if (!isNaN(id)) personIdFromRecord = id;
        }
      }

      return {
        personId: personIdFromRecord,
        receivablesAccount: null as number | null, // Will be resolved server-side during settlement
        invoiceDocNumber: docNumber,
        unpaidAmount: unpaidFromDesc,
      };
    } catch {
      return defaults;
    }
  }, [result]);
  void _evidence; // suppress TS6133 — used in future settlement flow

  const hasRecords = result.affectedRecords && result.affectedRecords.length > 0;
  const firstNavigableRoute = hasRecords
    ? result.affectedRecords!.map(r => getEntityRoute(r)).find((r): r is string => r !== null) ?? null
    : null;

  const handleCardClick = () => {
    if (firstNavigableRoute) {
      navigate(firstNavigableRoute);
    }
  };

  const handleFindPayments = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (invoiceJournalEntryId === null) return;

    setFindingPayments(true);
    setMatchesError(null);
    setSettleResult(null);
    setMatches([]);

    try {
      const data = await api.post<PaymentMatch[]>('/scanners/find-matching-payments', {
        invoiceJournalEntryId,
      });
      setMatches(data ?? []);
      setShowMatches(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to find matching payments';
      setMatchesError(msg);
      setShowMatches(true);
    } finally {
      setFindingPayments(false);
    }
  };

  const handleSettle = async (e: React.MouseEvent, match: PaymentMatch) => {
    e.stopPropagation();
    if (invoiceJournalEntryId === null) return;

    setSettling(match.journalEntryId);
    setSettleResult(null);

    try {
      // Use the auto-settle API which resolves accounts server-side from journal entry lines.
      // This ensures correct debit/credit accounts (bank → receivables) instead of the
      // old approach that created broken offsets with debit=credit=same receivables account.
      await api.post('/scanners/auto-settle', {
        invoiceJournalEntryId,
        bankJournalEntryId: match.journalEntryId,
      });

      setSettleResult({ success: true, message: `Settlement created: ${match.documentNumber} → invoice #${invoiceJournalEntryId}` });
      // Brief delay to ensure the database commit has propagated, then refetch scanner results
      await new Promise(resolve => setTimeout(resolve, 500));
      await queryClient.refetchQueries({ queryKey: ['scanner-results'] });
      // Clear the payment matches panel for immediate visual feedback
      setShowMatches(false);
      setMatches([]);
      setSettleResult(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create settlement';
      setSettleResult({ success: false, message: msg });
    } finally {
      setSettling(null);
    }
  };

  const handleCloseMatches = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMatches(false);
    setMatches([]);
    setMatchesError(null);
    setSettleResult(null);
  };

  return (
    <div
      onClick={handleCardClick}
      className={`bg-white border border-gray-200 rounded-lg p-3 transition-shadow ${
        firstNavigableRoute ? 'cursor-pointer hover:shadow-sm' : ''
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <SeverityBadge severity={result.severity} />
        <span className="text-xs text-gray-400">{result.type}</span>
        {firstNavigableRoute && (
          <span className="ml-auto text-xs text-primary-500 flex items-center gap-0.5">
            View
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        )}
      </div>
      <h3 className="text-sm font-semibold text-gray-900">{result.title}</h3>
      <p className="text-xs text-gray-600 mt-1">{result.description}</p>
      {result.suggestedAction && (
        <p className="text-xs text-primary-600 mt-2 font-medium">
          Suggested: {result.suggestedAction}
        </p>
      )}
      {hasRecords && result.affectedRecords!.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {result.affectedRecords!.map((record, i) => {
            const route = getEntityRoute(record);
            const slashIndex = record.indexOf('/');
            const type = slashIndex >= 0 ? record.substring(0, slashIndex) : record;
            const label = formatEntityType(type);
            return route ? (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); navigate(route); }}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary-50 text-primary-700 rounded hover:bg-primary-100 transition-colors border border-primary-200/50"
              >
                {label}
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <span key={i} className="inline-flex px-2 py-0.5 text-xs bg-gray-50 text-gray-500 rounded">
                {label}
              </span>
            );
          })}
        </div>
      )}

      {/* Settle button for overdue invoices */}
      {isOverdueInvoice && invoiceJournalEntryId !== null && (
        <div className="mt-3 pt-2 border-t border-gray-100">
          {!showMatches && (
            <button
              onClick={handleFindPayments}
              disabled={findingPayments}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {findingPayments ? (
                <>
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Finding payments...
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Settle
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Matching payments panel */}
      {showMatches && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-green-800">Matching payments found</span>
            <button
              onClick={handleCloseMatches}
              className="text-xs text-green-600 hover:text-green-800 font-medium"
            >
              Close
            </button>
          </div>

          {matchesError && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5 mb-2">
              {matchesError}
            </div>
          )}

          {settleResult && (
            <div className={`text-xs px-2 py-1.5 rounded mb-2 ${
              settleResult.success
                ? 'text-green-700 bg-green-100 border border-green-200'
                : 'text-red-700 bg-red-50 border border-red-100'
            }`}>
              {settleResult.message}
            </div>
          )}

          {!matchesError && matches.length === 0 && (
            <div className="text-xs text-gray-600">
              No matching payments found. You can create an offset manually.
              <button
                onClick={(e) => { e.stopPropagation(); navigate('/offsets'); }}
                className="ml-1 text-primary-600 hover:text-primary-700 underline font-medium"
              >
                Go to Offsets
              </button>
            </div>
          )}

          {matches.length > 0 && (
            <div className="space-y-1">
              {matches.map((match) => (
                <div
                  key={match.journalEntryId}
                  className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 px-2 py-2 md:py-1.5 bg-white/70 rounded hover:bg-white transition-colors"
                >
                  <div className="flex items-center gap-2 md:gap-3">
                    <span className="text-xs font-medium text-gray-800">{match.documentNumber}</span>
                    <span className="text-xs text-gray-500">{formatDate(match.date)}</span>
                  </div>
                  <div className="flex items-center gap-2 md:gap-3">
                    <span className="text-xs font-mono text-gray-900 text-right">{formatMoney(match.amount)}</span>
                    <span className={`text-xs font-mono text-right ${
                      Math.abs(match.amountDifference) < 0.01
                        ? 'text-green-600'
                        : 'text-amber-600 font-semibold'
                    }`}>
                      &Delta; {formatMoney(match.amountDifference)}
                    </span>
                  </div>
                  <button
                    onClick={(e) => handleSettle(e, match)}
                    disabled={settling === match.journalEntryId}
                    className="md:ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    {settling === match.journalEntryId ? (
                      <>
                        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Settling...
                      </>
                    ) : (
                      <>Settle with {match.documentNumber}</>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Agent Config Tab (real API) ---

function AgentConfigTab() {
  const queryClient = useQueryClient();
  const hasWorkspace = !!getWorkspaceId();

  const { data: configs, isLoading } = useQuery<AuthorityConfigDto[]>({
    queryKey: ['ai-authority-configs'],
    queryFn: aiAuthorityApi.getConfigs,
    staleTime: 60_000,
    enabled: hasWorkspace,
  });

  const [localConfigs, setLocalConfigs] = useState<Record<string, string>>({});

  const handleChange = (workflowName: string, authorityLevel: string) => {
    setLocalConfigs(prev => ({ ...prev, [workflowName]: authorityLevel }));
  };

  const handleSave = async () => {
    for (const [workflow, level] of Object.entries(localConfigs)) {
      await aiAuthorityApi.updateConfig(workflow, level);
    }
    setLocalConfigs({});
    queryClient.invalidateQueries({ queryKey: ['ai-authority-configs'] });
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">Configure AI agent authority levels per workflow.</p>
          <p className="text-xs text-gray-400 mt-1">
            Authority determines what actions each agent can take. Default is &quot;suggest&quot; until explicitly promoted.
          </p>
        </div>
        {Object.keys(localConfigs).length > 0 && (
          <button
            className="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 border border-primary-200"
            onClick={handleSave}
          >
            Save Changes ({Object.keys(localConfigs).length})
          </button>
        )}
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="dense w-full">
          <thead>
            <tr>
              <th className="text-left">Workflow</th>
              <th className="text-left">Authority Level</th>
              <th className="text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            {configs?.map((config) => {
              const currentLevel = localConfigs[config.workflowName] ?? config.authorityLevel;
              return (
                <tr key={config.workflowName}>
                  <td className="font-medium">{config.workflowName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                  <td>
                    <select
                      value={currentLevel}
                      onChange={(e) => handleChange(config.workflowName, e.target.value)}
                      className="border border-gray-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="Observe">Observe</option>
                      <option value="Suggest">Suggest</option>
                      <option value="ActWithReview">Act with Review</option>
                      <option value="ActAutonomous">Act Autonomous</option>
                    </select>
                  </td>
                  <td className="text-sm text-gray-600">{config.description}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 mt-3">
        Authority changes are audit-logged. All changes require tenant admin privileges.
      </p>
    </div>
  );
}

// --- Policy Versions Tab (stub) ---

function PolicyVersionsTab() {
  const policies = [
    { name: 'Chronology Policy', version: 'v3', effectiveDate: '2025-01-01', description: 'Controls operation dates relative to closing dates' },
    { name: 'VAT Rules (Lithuania)', version: 'v2025-07', effectiveDate: '2025-07-01', description: 'Lithuanian VAT rates: 21%, 9%, 5%, 0%' },
    { name: 'Journal Entry Invariants', version: 'v1', effectiveDate: '2025-01-01', description: 'Debit=credit, 2dp rounding, no negative amounts' },
    { name: 'Document Numbering', version: 'v2', effectiveDate: '2025-01-01', description: 'Sequential numbering per type/serial/date' },
    { name: 'Period Close Rules', version: 'v2', effectiveDate: '2025-01-01', description: 'Year-end closing account requirements' },
    { name: 'Scanner Thresholds', version: 'v1', effectiveDate: '2025-01-01', description: 'Overdue days, materiality, VAT tolerance' },
  ];

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        Policy versions track which ruleset was in effect when each AI decision was made.
      </p>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="dense w-full">
          <thead>
            <tr>
              <th className="text-left">Policy</th>
              <th className="text-left">Version</th>
              <th className="text-left">Effective</th>
              <th className="text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            {policies.map((policy) => (
              <tr key={policy.name}>
                <td className="font-medium">{policy.name}</td>
                <td>
                  <span className="inline-flex px-2 py-0.5 bg-gray-100 rounded text-xs font-mono font-medium text-gray-700">
                    {policy.version}
                  </span>
                </td>
                <td className="whitespace-nowrap">{formatDate(policy.effectiveDate)}</td>
                <td className="text-gray-600 max-w-64 truncate">{policy.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 mt-3">
        All policy versions are immutable once created. New versions supersede previous ones with explicit effective dates.
      </p>
    </div>
  );
}

// --- Commander Tab ---

interface CommanderStatus {
  watchdog: { running: boolean; pid?: number; lastCheck?: string; lastTrigger?: string; commanderRunning: boolean; commanderPid?: number; activeReports?: number; processedToday?: number; recentLog?: string[] };
  progress: { currentPhase: string; activeAgent?: string | null; startedAt?: string; updatedAt?: string; reviewTitle?: string; items?: { task: string; status: string; agent?: string }[]; buildStatus?: string; testStatus?: string; lastCommit?: string; log?: string[] };
  activeReports: { filename: string; type: string; status: string; priority: string; title: string; created?: string }[];
  summary: { watchdogRunning: boolean; commanderRunning: boolean; totalReports: number; openReports: number; inProgressReports: number; processedToday: number };
}

function CommanderTab() {
  const aiBridgeUrl = import.meta.env.VITE_AI_BRIDGE_URL ?? `http://${window.location.hostname}:5291`;
  const queryClient = useQueryClient();
  const [rebooting, setRebooting] = React.useState(false);
  const [rejecting, setRejecting] = React.useState<string | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery<CommanderStatus>({
    queryKey: ['commander-status'],
    queryFn: () => fetch(`${aiBridgeUrl}/status`).then(r => r.json()),
    refetchInterval: 15000,
  });

  const handleReboot = async () => {
    if (!confirm('Reboot all services? The watchdog will restart API, AiBridge, and Frontend (watchdog stays running).')) return;
    setRebooting(true);
    try {
      await fetch(`${aiBridgeUrl}/reboot`, { method: 'POST' });
      setTimeout(() => { setRebooting(false); refetch(); }, 20000);
    } catch {
      setRebooting(false);
    }
  };

  const handleRejectReport = async (filename: string) => {
    if (!confirm(`Reject and remove this report?\n${filename}`)) return;
    setRejecting(filename);
    try {
      const resp = await fetch(`${aiBridgeUrl}/reports/${encodeURIComponent(filename)}`, { method: 'DELETE' });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({ error: 'Unknown error' }));
        alert(`Failed to reject report: ${data.error || resp.statusText}`);
      }
      refetch();
    } catch (err) {
      alert(`Failed to reject report: ${err instanceof Error ? err.message : 'Network error'}`);
    } finally {
      setRejecting(null);
    }
  };

  if (isLoading) return <div className="text-sm text-gray-500 p-4">Loading status...</div>;
  if (error) return <div className="text-sm text-red-600 p-4">Failed to connect to AiBridge: {String(error)}</div>;
  if (!data) return null;

  const { watchdog, progress, activeReports, summary } = data;

  return (
    <div className="space-y-6">
      {/* Action buttons */}
      <div className="flex justify-end gap-2">
        <button
          onClick={handleReboot}
          disabled={rebooting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className={`w-4 h-4 ${rebooting ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {rebooting ? 'Restarting...' : 'Reboot Services'}
        </button>
        <button
          onClick={() => { refetch(); queryClient.invalidateQueries({ queryKey: ['commander-status'] }); }}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {isFetching ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Status indicators */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatusCard label="Watchdog" value={summary.watchdogRunning ? 'Running' : 'Stopped'} color={summary.watchdogRunning ? 'green' : 'red'} />
        <StatusCard label="Commander" value={summary.commanderRunning ? 'Working' : 'Idle'} color={summary.commanderRunning ? 'amber' : 'gray'} />
        <StatusCard label="Queue" value={`${summary.totalReports} reports`} color={summary.totalReports > 0 ? 'blue' : 'gray'} />
        <StatusCard label="Processed Today" value={`${summary.processedToday ?? 0}`} color="gray" />
      </div>

      {/* Current phase — always visible */}
      <div className="bg-white rounded-lg border p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Current Work</h3>
        {progress.reviewTitle && (
          <div className="text-xs text-primary-600 font-medium mb-1 truncate" title={progress.reviewTitle}>
            📋 {progress.reviewTitle}
          </div>
        )}
        <div className={`text-lg font-medium ${progress.currentPhase === 'Idle' ? 'text-gray-400' : 'text-primary-700'}`}>
          {progress.currentPhase !== 'Idle' ? progress.currentPhase : summary.commanderRunning ? 'Commander is processing reports...' : 'Idle — waiting for next cycle'}
        </div>
        {progress.activeAgent && (
          <div className="flex items-center gap-1.5 mt-2">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-sm font-medium text-amber-700">Running: {progress.activeAgent}</span>
          </div>
        )}
        {progress.updatedAt && (
          <div className="text-xs text-gray-400 mt-1">Last update: {new Date(progress.updatedAt).toLocaleTimeString()}</div>
        )}
        {/* Progress items */}
        {progress.items && progress.items.length > 0 && (
          <div className="mt-3 space-y-1">
            {progress.items.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span className={`w-2 h-2 rounded-full ${item.status === 'completed' ? 'bg-green-500' : item.status === 'in_progress' ? 'bg-amber-500 animate-pulse' : item.status === 'stale' ? 'bg-gray-400' : 'bg-gray-300'}`} />
                <span className={item.status === 'completed' ? 'text-gray-400 line-through' : item.status === 'stale' ? 'text-gray-500 italic' : 'text-gray-700'}>{item.task}{item.status === 'stale' ? ' (stale)' : ''}</span>
                {item.agent && <span className="text-xs text-gray-400 ml-auto">{item.agent}</span>}
              </div>
            ))}
          </div>
        )}
        {/* Build/test status */}
        <div className="flex gap-4 mt-3 text-xs text-gray-500">
          {progress.buildStatus && <span>Build: <span className={progress.buildStatus === 'passing' ? 'text-green-600' : 'text-red-600'}>{progress.buildStatus}</span></span>}
          {progress.testStatus && <span>Tests: <span className={progress.testStatus === 'passing' ? 'text-green-600' : progress.testStatus === 'pending' ? 'text-gray-400' : 'text-red-600'}>{progress.testStatus}</span></span>}
          {progress.lastCommit && <span>Commit: <code className="text-xs">{progress.lastCommit}</code></span>}
        </div>
      </div>

      {/* Active Reports — always visible, shows processing status */}
      <div className="bg-white rounded-lg border p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          Active Reports
          {summary.inProgressReports > 0 && (
            <>
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
              </span>
              <span className="text-xs font-normal text-amber-600">({summary.inProgressReports} in progress)</span>
            </>
          )}
        </h3>
        {activeReports.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-sm">
            No active reports in the queue.
            <br />
            <span className="text-xs">Reports submitted via the AI Copilot or dropped into the active folder will appear here.</span>
          </div>
        ) : (
          <div className="space-y-2">
            {activeReports.map((report, i) => (
              <div key={i} className={`flex items-center gap-3 p-2 rounded ${
                report.status === 'in-progress'
                  ? 'bg-amber-50 border-l-4 border-l-amber-400 shadow-sm shadow-amber-100'
                  : report.status === 'queued'
                    ? 'bg-primary-50 border-l-4 border-l-primary-300'
                    : 'bg-gray-50'
              }`}>
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                  report.status === 'in-progress' ? 'bg-amber-100 text-amber-700' :
                  report.status === 'open' ? 'bg-primary-100 text-primary-700' :
                  report.status === 'queued' ? 'bg-primary-100 text-primary-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {report.status === 'queued' ? 'queued' : report.status}
                </span>
                <span className={`inline-flex px-1.5 py-0.5 rounded text-xs ${
                  report.priority === 'critical' ? 'bg-red-100 text-red-700' :
                  report.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {report.priority}
                </span>
                <span className="text-sm text-gray-700 flex-1">{report.title}</span>
                <span className="text-xs text-gray-400">{report.type}</span>
                <button
                  onClick={() => handleRejectReport(report.filename)}
                  disabled={rejecting === report.filename}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Reject and remove report"
                >
                  {rejecting === report.filename ? (
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  )}
                  {rejecting === report.filename ? 'Removing...' : 'Delete'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent log */}
      {watchdog.recentLog && watchdog.recentLog.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Watchdog Log</h3>
          <div className="font-mono text-xs text-gray-500 max-h-48 overflow-y-auto space-y-0.5">
            {watchdog.recentLog.slice().reverse().map((line, i) => (
              <div key={i} className={line.includes('WARN') ? 'text-amber-600' : line.includes('ERROR') ? 'text-red-600' : ''}>{line}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusCard({ label, value, color }: { label: string; value: string; color: 'green' | 'red' | 'amber' | 'gray' | 'blue' }) {
  const colors = {
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
    amber: 'bg-amber-50 border-amber-200',
    gray: 'bg-gray-50 border-gray-200',
    blue: 'bg-primary-50 border-primary-200',
  };
  const dotColors = {
    green: 'bg-green-500',
    red: 'bg-red-500',
    amber: 'bg-amber-500 animate-pulse',
    gray: 'bg-gray-400',
    blue: 'bg-primary-500',
  };
  return (
    <div className={`rounded-lg border p-3 ${colors[color]}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="flex items-center gap-2 mt-1">
        <span className={`w-2 h-2 rounded-full ${dotColors[color]}`} />
        <span className="text-sm font-medium">{value}</span>
      </div>
    </div>
  );
}

// --- Main Page ---

export default function AIAudit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabId) || 'commander';
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI & Audit</h1>
          <p className="text-sm text-gray-500 mt-1">
            Agent configuration, decision log, exceptions, and policy versions
          </p>
        </div>
        <div className="text-xs text-gray-400">
          {today()}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="mt-4">
        {activeTab === 'decision-log' && <DecisionLogTab />}
        {activeTab === 'exceptions' && <ExceptionsTab />}
        {activeTab === 'commander' && <CommanderTab />}
        {activeTab === 'agent-config' && <AgentConfigTab />}
        {activeTab === 'policy-versions' && <PolicyVersionsTab />}
      </div>
    </div>
  );
}
